import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import axiosInstance from "../utils/api";
import toast from "react-hot-toast";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getSessionUser } from "../utils/session";

const ACCESS_OPTIONS = [
  { label: "Dashboard", value: "dashboard" },
  { label: "Stand List", value: "stands" },
  { label: "Retro Plan", value: "retro-plan" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Security", value: "security" },
  { label: "Badging", value: "badging" },
  { label: "History", value: "history" },
];

const ALWAYS_AVAILABLE = ["dashboard", "stands", "history"];

const FEATURE_TO_ACCESS = {
  retroplanning: "retro-plan",
  cleaning: "cleaning",
  hse: "security",
  badging: "badging",
};

const StaffModal = ({ isOpen, onClose, eventId, staff, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "sub_admin",
    accesses: [],
    halls: [],
  });
  const [hallOptions, setHallOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { permissions: adminPermissions = [] } = getSessionUser();
  const allowedGatedAccesses = adminPermissions
    .map((key) => FEATURE_TO_ACCESS[key])
    .filter(Boolean);
  const availableAccessOptions = ACCESS_OPTIONS.filter(
    (opt) => ALWAYS_AVAILABLE.includes(opt.value) || allowedGatedAccesses.includes(opt.value)
  );

  useEffect(() => {
    if (!eventId) return;

    const fetchEvent = async () => {
      try {
        const res = await axiosInstance.get(`/event/${eventId}`);
        const hallsNumber = res.data.event.hallsNumber || 0;

        const halls = Array.from({ length: hallsNumber }, (_, i) => `Hall ${i + 1}`);
        setHallOptions(halls);
      } catch (err) {
        console.error("Failed to load halls", err);
      }
    };

    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (staff) {
      setForm({
        name: staff.name || "",
        email: staff.email || "",
        phone: staff.phone || "",
        role: staff.role || "sub_admin",
        accesses: staff.accesses || [],
        halls: staff.halls || [],
      });
    } else {
      setForm({
        name: "",
        email: "",
        phone: "",
        role: "sub_admin",
        accesses: [],
        halls: [],
      });
    }
  }, [staff]);

  const toggleAccess = (access) => {
    setForm((prev) => ({
      ...prev,
      accesses: prev.accesses.includes(access)
        ? prev.accesses.filter((a) => a !== access)
        : [...prev.accesses, access],
    }));
  };

  const toggleHall = (hall) => {
    setForm((prev) => ({
      ...prev,
      halls: prev.halls.includes(hall)
        ? prev.halls.filter((h) => h !== hall)
        : [...prev.halls, hall],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      if (staff) {
        await axiosInstance.put(`/auth/user/${staff._id}`, {
          name: form.name,
          phone: form.phone,
          role: form.role,
          accesses: form.accesses,
          halls: form.halls,
        });

        toast.success("Staff updated successfully");
        onClose();
        onSuccess();
      } else {
        const res = await axiosInstance.post("/auth/register", {
          ...form,
          event: eventId,
        });

        toast.success("Staff added successfully");
        onClose();
        onSuccess({
          name: res.data.user.name,
          email: res.data.user.email,
          plainPassword: res.data.plainPassword,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 " +
    "focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition disabled:bg-gray-50 disabled:text-gray-400";

  const labelClass = "block text-xs font-medium text-gray-500 mb-1.5";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={staff ? "Edit Staff" : "Add Staff"}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 px-2">
        <div>
          <label className={labelClass}>Full name</label>
          <input
            type="text"
            placeholder="e.g. Sara El Amrani"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputClass}
            required
          />
        </div>

        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            placeholder="name@company.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputClass}
            required
            disabled={!!staff}
          />
        </div>

        <div>
          <label className={labelClass}>Phone (optional)</label>
          <PhoneInput
            placeholder="Enter phone number"
            value={form.phone}
            onChange={(value) => setForm({ ...form, phone: value })}
            defaultCountry="MA"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Role</label>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className={inputClass + " bg-white"}
          >
            <optgroup label="Web">
              <option value="sub_admin">Admin</option>
              <option value="orga">Organisation</option>
            </optgroup>
            <optgroup label="Mobile">
              <option value="floor-manager">Floor Manager</option>
              <option value="hse">HSE</option>
              <option value="cleaning">Cleaning</option>
              <option value="security">Security</option>
            </optgroup>
          </select>
        </div>

        {form.role === "sub_admin" && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm text-gray-600">
              Admin has all permissions — no access selection needed.
            </p>
          </div>
        )}

        {form.role === "orga" && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <p className="text-xs font-medium text-gray-500">Access</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {availableAccessOptions.map((item) => (
                <label
                  key={item.value}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={form.accesses.includes(item.value)}
                    onChange={() => toggleAccess(item.value)}
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/20"
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {form.role === "floor-manager" && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <p className="text-xs font-medium text-gray-500">Assigned Halls</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {hallOptions.map((hall) => (
                <label
                  key={hall}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={form.halls.includes(hall)}
                    onChange={() => toggleHall(hall)}
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/20"
                  />
                  {hall}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? staff
                ? "Updating..."
                : "Creating..."
              : staff
                ? "Update Staff"
                : "Add Staff"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default StaffModal;